<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero (mismo que la vista de Novedades) -->
    <ImageBackgroundSection
      seed="noticias-hero"
      :image-url="heroImageUrl"
      :loading-image="heroImageLoading"
      alt="Novedades"
      :title="heroMedia?.title || 'Novedades'"
      :subtitle="
        heroMedia?.subtitle ||
        'Enterate de las novedades, logros y acontecimientos que impulsan a las bochas cordobesas'
      "
    />

    <router-link
      v-if="showAdminBack"
      :to="adminBackUrl"
      class="admin-return-tab group fixed left-0 top-1/2 z-40 inline-flex -translate-y-1/2 items-center gap-0 overflow-hidden rounded-r-2xl border border-l-0 border-white/12 bg-[#0f172a]/90 p-2 text-sm font-semibold text-white shadow-[0_1rem_2.5rem_rgba(0,0,0,0.34)] backdrop-blur-xl transition-colors duration-300 hover:bg-[#111c31]"
    >
      <span
        class="relative z-10 inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-bochas-rojo via-red-500 to-bochas-azul shadow-[0_0_1.25rem_rgba(165,33,33,0.42)]"
      >
        <IconFbochas name="chevron-left" :size="18" />
      </span>
      <span
        class="relative z-10 ml-0 flex max-w-0 flex-col overflow-hidden whitespace-nowrap leading-tight opacity-0 transition-all duration-300 ease-out group-hover:ml-3 group-hover:max-w-36 group-hover:opacity-100"
      >
        <span class="text-[10px] uppercase tracking-wide text-white/55">
          Admin
        </span>
        <span>Volver al panel</span>
      </span>
    </router-link>

    <!-- Loading -->
    <section v-if="loading" class="py-24 sm:py-32 text-center">
      <div
        class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
      ></div>
    </section>

    <!-- Error -->
    <section v-else-if="error" class="max-w-7xl mx-auto px-4 py-12 lg:py-24">
      <div
        class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4"
      >
        {{ error }}
      </div>
      <div class="mt-6">
        <VolverButton :to="backUrl" :text="`Volver a ${backLabel}`" />
      </div>
    </section>

    <!-- Detalle -->
    <RevealOnScroll
      v-else-if="noticia"
      as="article"
      :duration="700"
      :distance="24"
      class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:py-8 sm:py-12"
    >
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <!-- Columna principal (2/3) -->
        <div class="lg:col-span-2">
          <!-- Fecha + categoría (arriba) -->
          <div
            class="flex items-center gap-2 text-lg sm:text-sm text-gray-400 mb-2"
          >
            <time>{{ formatNewsDate(noticia.published_at) }}</time>
            <span v-if="noticia.tag">· {{ noticia.tag.name }}</span>
          </div>

          <!-- Título -->
          <h1
            class="text-2xl sm:text-3xl lg:text-4xl mb-4 font-semibold text-white"
          >
            {{ noticia.title }}
          </h1>

          <!-- Descripción corta -->
          <p
            v-if="noticia.description"
            class="text-base sm:text-lg mb-4 text-gray-300 italic"
          >
            {{ noticia.description }}
          </p>

          <!-- Acciones sociales -->
          <div
            class="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 mb-5 sm:mb-6 border-y border-white/10"
          >
            <button
              @click="compartirWhatsapp"
              type="button"
              class="hover:opacity-80 transition-opacity"
              aria-label="Compartir en WhatsApp"
            >
              <IconFbochas name="whatsapp" :size="22" color="#a52121" />
            </button>
            <button
              @click="compartirFacebook"
              type="button"
              class="hover:opacity-80 transition-opacity"
              aria-label="Compartir en Facebook"
            >
              <IconFbochas name="facebook" :size="22" color="#a52121" />
            </button>
            <button
              @click="abrirInstagram"
              type="button"
              class="hover:opacity-80 transition-opacity"
              aria-label="Ir a Instagram"
            >
              <IconFbochas name="instagram" :size="22" color="#a52121" />
            </button>
          </div>

          <!-- Imagen de portada -->
          <div
            v-if="coverImage"
            class="w-full h-64 sm:h-80 lg:h-96 mb-5 sm:mb-4 overflow-hidden rounded-xl bg-bochas-bg"
          >
            <img
              :src="coverImage.url"
              :alt="noticia.title"
              class="w-full h-full object-cover"
            />
          </div>

          <!-- Contenido HTML (del editor Quill) -->
          <section class="content-html mb-4" v-html="sanitizedContent" />

          <!-- Galería adicional (si hay más imágenes además de la portada) -->
          <section v-if="galería.length > 0">
            <h2 class="text-lg sm:text-xl font-bold text-white mb-4">
              Galería
            </h2>
            <div
              class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
            >
              <button
                v-for="(img, idx) in galería"
                :key="img.id"
                @click="openLightbox(idx)"
                class="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-700 hover:border-bochas-rojo transition-colors group"
              >
                <img
                  :src="img.url"
                  :alt="`Imagen ${idx + 1}`"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
              </button>
            </div>
          </section>

          <!-- Volver (solo abajo) -->
          <div class="py-10 sm:py-24">
            <VolverButton :to="backUrl" />
          </div>
        </div>

        <!-- Sidebar: noticias relacionadas (1/3) -->
        <aside class="mb-12 sm:mb-0 lg:col-span-1">
          <h2
            class="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2"
          >
            <span
              class="w-1 h-5 sm:h-6 bg-bochas-rojo rounded-full inline-block"
            ></span>
            Noticias relacionadas
          </h2>
          <div v-if="relacionadas.length === 0" class="text-sm text-gray-400">
            No hay más noticias por ahora.
          </div>
          <ul v-else class="space-y-3">
            <li
              v-for="nota in relacionadas"
              :key="nota.id"
              class="card-fbochas overflow-hidden"
            >
              <router-link
                :to="`/noticias/${nota.id}`"
                class="flex flex-row gap-3 p-2 sm:p-3 hover:bg-white/5 transition-colors"
              >
                <div
                  class="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 overflow-hidden rounded-lg bg-bochas-bg"
                >
                  <img
                    v-if="nota.cover_image"
                    :src="nota.cover_image"
                    :alt="nota.title"
                    class="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div v-else class="w-full h-full bg-degradado-bochas"></div>
                </div>
                <div class="flex-1 min-w-0">
                  <p
                    v-if="nota.tag"
                    class="text-[10px] font-semibold uppercase tracking-wide truncate mb-1"
                    :style="{ color: nota.tag.color || '#A52121' }"
                  >
                    {{ nota.tag.name }}
                  </p>
                  <h3
                    class="text-xs sm:text-sm font-semibold text-white line-clamp-2 mb-1"
                  >
                    {{ nota.title }}
                  </h3>
                  <time class="text-[10px] sm:text-xs text-gray-400">
                    {{ formatNewsDate(nota.published_at) }}
                  </time>
                </div>
              </router-link>
            </li>
          </ul>
        </aside>
      </div>
    </RevealOnScroll>

    <!-- Lightbox -->
    <Teleport to="body">
      <div
        v-if="lightboxIndex !== null && galería[lightboxIndex]"
        @click="closeLightbox"
        class="modal-backdrop-animated fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
      >
        <button
          @click.stop="
            lightboxIndex =
              (lightboxIndex - 1 + galería.length) % galería.length
          "
          class="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white text-3xl sm:text-4xl px-3 py-2 hover:bg-white/10 rounded-full"
        >
          ‹
        </button>
        <img
          v-bottom-sheet="closeLightbox"
          :src="galería[lightboxIndex].url"
          :alt="`Imagen ${lightboxIndex + 1}`"
          class="mobile-lightbox-content max-h-full max-w-full object-contain"
          @click.stop
        />
        <button
          @click.stop="lightboxIndex = (lightboxIndex + 1) % galería.length"
          class="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white text-3xl sm:text-4xl px-3 py-2 hover:bg-white/10 rounded-full"
        >
          ›
        </button>
        <button
          @click.stop="closeLightbox"
          class="absolute top-2 right-2 sm:top-4 sm:right-4 text-white text-2xl sm:text-3xl px-3 py-2 hover:bg-white/10 rounded-full"
        >
          ✕
        </button>
        <div
          class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-xs sm:text-sm"
        >
          {{ lightboxIndex + 1 }} / {{ galería.length }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import {
  obtenerNoticia,
  listarNoticias,
} from "@/services/noticias/noticiasService";
import { formatDate } from "@/utils/date";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import VolverButton from "@/components/Common/VolverButton.vue";
import { closeBottomSheet } from "@/directives/bottomSheet";
import { useSiteMediaImageState } from "@/composables/useSiteMediaImage";
import { sanitizeNewsContent } from "@/utils/newsContent";
import { applyContentSeo, applyNotFoundSeo } from "@/services/seo/seoService";

const route = useRoute();
const {
  imageUrl: heroImageUrl,
  item: heroMedia,
  loading: heroImageLoading,
} = useSiteMediaImageState("noticias.hero");
const noticia = ref(null);
const relacionadas = ref([]);
const loading = ref(true);
const error = ref("");
const lightboxIndex = ref(null);
const sanitizedContent = computed(() =>
  sanitizeNewsContent(noticia.value?.content),
);
const numericDateOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

// Determina el "Volver" según el contexto (?from=historias → /historias, sino /noticias)
const backUrl = computed(() =>
  route.query.from === "historias" ? "/historias" : "/noticias",
);
const backLabel = computed(() =>
  route.query.from === "historias" ? "historias" : "noticias",
);
const showAdminBack = computed(() => route.query.fromAdmin === "noticias");
const adminBackUrl = computed(() => "/admin/noticias");

// Imágenes de la galería (todas menos la portada, que ya está cómo hero)
const galería = computed(() => {
  if (!noticia.value?.images) return [];
  return noticia.value.images
    .filter((img) => !img.is_cover)
    .sort((a, b) => a.order - b.order);
});

// Imagen de portada: la que tenga is_cover=true.
// Si no hay ninguna marcada, fallback a la primera.
// Si no hay imágenes, null (el v-if no renderiza nada).
const coverImage = computed(() => {
  if (!noticia.value?.images || noticia.value.images.length === 0) return null;
  return (
    noticia.value.images.find((img) => img.is_cover) || noticia.value.images[0]
  );
});

function formatNewsDate(date) {
  return formatDate(date, numericDateOptions);
}

function openLightbox(idx) {
  lightboxIndex.value = idx;
  document.body.style.overflow = "hidden";
}
function closeLightbox() {
  closeBottomSheet(() => {
    lightboxIndex.value = null;
    document.body.style.overflow = "";
  });
}

// ===== Compartir (funcionales; URL = window.location.href por ahora) =====
function compartirWhatsapp() {
  const url = window.location.href;
  const text = noticia.value?.title || "";
  window.open(
    `https://api.whatsapp.com/send?text=${encodeURIComponent(
      text + " " + url,
    )}`,
    "_blank",
    "noopener,noreferrer",
  );
}

function compartirFacebook() {
  const url = window.location.href;
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    "_blank",
    "noopener,noreferrer,width=600,height=400",
  );
}

function abrirInstagram() {
  window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
}

// ===== Carga de noticias relacionadas (últimas 4, excluyendo la actual) =====
async function loadRelacionadas(currentId) {
  try {
    // Pido 8 para poder filtrar la actual y aún así tener 4
    const data = await listarNoticias({ limit: 8, type: "NOTICIA" });
    const items = (data.items || [])
      .filter((n) => String(n.id) !== String(currentId))
      .slice(0, 4);
    relacionadas.value = items;
  } catch {
    relacionadas.value = [];
  }
}

async function load(id) {
  loading.value = true;
  error.value = "";
  noticia.value = null;
  relacionadas.value = [];
  try {
    noticia.value = await obtenerNoticia(id);
    const shareImage =
      noticia.value.cover_image ||
      noticia.value.images?.find((image) => image.is_cover)?.url ||
      noticia.value.images?.[0]?.url;
    await applyContentSeo({
      title: noticia.value.title,
      description: noticia.value.description,
      content: noticia.value.content,
      image: shareImage,
    });
    loadRelacionadas(id);
  } catch (err) {
    applyNotFoundSeo();
    error.value =
      err.response?.status === 404
        ? "Noticia no encontrada."
        : "Error al cargar la noticia.";
  } finally {
    loading.value = false;
  }
}

// Cerrar lightbox con Escape
function onKeydown(e) {
  if (e.key === "Escape") closeLightbox();
}

onMounted(() => {
  load(route.params.id);
  window.addEventListener("keydown", onKeydown);
});
watch(
  () => route.params.id,
  (id) => load(id),
);
</script>

<style scoped>
.admin-return-tab::before {
  position: absolute;
  inset: 0;
  pointer-events: none;
  content: "";
  background: linear-gradient(90deg, rgba(165, 33, 33, 0.2), transparent 55%);
}
</style>

<style scoped>
.content-html {
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.content-html :deep(h1) {
  font-size: 1.875rem;
  font-weight: 700;
  color: white;
  margin-top: 2rem;
  margin-bottom: 1rem;
}
.content-html :deep(h2) {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}
.content-html :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}
.content-html :deep(h4) {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}
.content-html :deep(p) {
  color: #d1d5db;
  line-height: 1.7;
  margin-bottom: 1rem;
  font-size: 1rem;
  overflow-wrap: break-word;
  word-wrap: break-word;
  max-width: 100%;
}
.content-html :deep(ul),
.content-html :deep(ol) {
  color: #d1d5db;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
  padding-left: 0.5rem;
}
.content-html :deep(ul) {
  list-style: disc;
}
.content-html :deep(ol) {
  list-style: decimal;
}
.content-html :deep(li) {
  margin-bottom: 0.4rem;
  line-height: 1.6;
}
.content-html :deep(a) {
  color: #f87171;
  text-decoration: underline;
}
.content-html :deep(a:hover) {
  color: #fca5a5;
}
.content-html :deep(blockquote) {
  border-left: 4px solid #a52121;
  padding: 0.5rem 0 0.5rem 1rem;
  color: #9ca3af;
  font-style: italic;
  margin: 1rem 0;
}
.content-html :deep(code) {
  background-color: #1f2937;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: #fbbf24;
  font-family: monospace;
}
.content-html :deep(pre) {
  background-color: #1f2937;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1rem 0;
}
.content-html :deep(pre code) {
  background: none;
  padding: 0;
  color: #e5e7eb;
}
.content-html :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 1rem 0;
}
.content-html :deep(hr) {
  border-color: #374151;
  margin: 2rem 0;
}
.content-html :deep(strong) {
  color: white;
  font-weight: 700;
}
.content-html :deep(em) {
  font-style: italic;
}
.content-html :deep(u) {
  text-decoration: underline;
}
.content-html :deep(s) {
  text-decoration: line-through;
}
</style>
